import { AnimatePresence, motion, Variants } from 'framer-motion';
import React, { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ReactComponent as CloseCrossIcon } from '/src/assets/svg/icons/close-cross.svg';

import './modal.css';
interface ModalProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  className?: string;
  closeButtonVisibility?: boolean;
  showCloseButtonForMobile?: boolean;
  background?: 'transparent' | 'default';
  type?: 'top' | 'middle' | 'full' | 'feedback';
  direction?: 'up' | 'down' | 'none';
  onClose?: () => void;
  isOpen: boolean;
  isMobile?: boolean;
  hasInAnimated?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  children,
  isOpen,
  className,
  background = 'transparent',
  closeButtonVisibility = true,
  showCloseButtonForMobile = false,
  type = 'top',
  onClose,
  direction = 'up',
  isMobile = false,
  hasInAnimated = false,
  style,
}) => {

 
  const modalAnimations: Variants = {
    initial: {
      opacity: 0,
      transition: { duration: 0.2 },
    },
    visible: {
      opacity: 1,
      transition: { duration: 0.2 },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.2, delay: 0.3 },
    },
  };

  const modalBodyAnimations: Variants = {
    initial: {
      opacity: 0,
      y: direction === 'up' ? -100 : direction === 'down' ? 100 : 0,
      transition: {
        ease: 'anticipate',
      },
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        ease: 'anticipate',
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      y: direction === 'up' ? -100 : direction === 'down' ? 100 : 0,
      transition: {
        ease: 'anticipate',
        duration: 0.3,
      },
    },
  };

  useEffect(() => {
    // Add or remove 'modal-open' class to the body based on isOpen
    // This can be used to prevent background scrolling when modal is open
    const body = document.querySelector('body');

    if (!body) return; // safety check

    body.classList.toggle('modal-open', isOpen);

    return () => {
      body.classList.remove('modal-open');
    };
  }, [isOpen]);

  if (typeof document === 'undefined') {
    return null;
  }
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          data-testid='modal'
          className={`modal ${background} ${hasInAnimated ? 'InAnimated' : ''}`}
          key='modal'
          variants={modalAnimations}
          initial='initial'
          animate='visible'
          exit='exit'
          onClick={(e) => e.currentTarget === e.target && onClose && onClose()}
        >
          <motion.div
            className={`modal-container ${type} ${className || ''}`}
            variants={modalBodyAnimations}
            initial='initial'
            animate='visible'
            exit='exit'
            style={style}
          >
            {closeButtonVisibility && (
              <div
                className={` ${
                  showCloseButtonForMobile ? '' : 'mobile-hidden'
                }`}
                style={{
                  position: 'absolute',
                  top: isMobile ? '10px' : '20px',
                  right: isMobile ? '10px' : '20px',
                }}
              >
                
                <CloseCrossIcon onClick={onClose} style={{ cursor: 'pointer' }} width={isMobile ? '24px' : '32px'} height={isMobile ? '24px' : '32px'} />
              </div>
            )}
            <div className='modal-body left'>{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default Modal;
